import { GambitCategory, GambitRepeat, GambitType } from '@/core/data/gambit/gambit.type';
import { ObjectType } from '@/core/data/localization/localization.type';
import { localize, localizeType, localizeWithValues } from '@/core/gambits';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamChipType } from '@/views/composables/models/components/GamChip';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import { addWeeks, format, isThisWeek, isToday, isTomorrow, isWithinInterval, startOfWeek } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { GambitSparkState, type BaseGambitDto, type DetailGambitDto } from '../data/gambit/gambit.interface';

type Day = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export function isNextWeek(date: Date, weekStartsOn: Day = 0): boolean {
  const start = startOfWeek(addWeeks(new Date(), 1), { weekStartsOn });
  const end = addWeeks(start, 1);
  return isWithinInterval(date, { start, end });
}

export const pluralize = (singular: string, plural: string, count: number) => {
  if (count === 1) return singular;

  return plural;
};

interface FormatTimeOptions {
  omitTime?: boolean;
  omitDay?: boolean;
  omitDate?: boolean;
  omitCloseDays?: boolean;
  shortDays?: boolean;
}

interface PlayerTagOptions {
  highlightAttendance?: boolean;
  size?: GamChipSize;
  isShort?: boolean;
}

export function getPlayerTag(gambit: BaseGambitDto | GambitDetailDto, options?: PlayerTagOptions): GamChipType[] {
  const { gambitTag } = GambitHelper;

  if (!gambit) return [];

  const highlightAttendance =
    typeof options?.highlightAttendance === 'boolean' ? options.highlightAttendance : gambit.isAttending;

  // const blockedCount =
  //   (gambit as GambitDetailDto).attendees?.filter((a) => a === null || a.username === 'unknown').length || 0;

  const blockedCount = 0;

  const subtractAttendees =
    (gambit.isAttending ? blockedCount : 0) + (gambit.isAttending && highlightAttendance ? 1 : 0);

  // Defaults
  const icon = GamIconName.PAWN;
  let variant = GamChipVariant.DEFAULT;
  const { isShort, size } = options || {};

  // Subtract blocked attendees
  const adjustedCount = gambit.attendeeCount - subtractAttendees;

  if (!gambit.isAttending && !adjustedCount) return [];

  // Highlight my attendance (Gambit list use-case)
  if (highlightAttendance && gambit.isAttending) {
    variant = GamChipVariant.PRIMARY;

    if (options?.isShort) {
      if (adjustedCount === 0) {
        return [gambitTag('You', { size, icon, variant })];
      }

      return [gambitTag(`You & ${adjustedCount}`, { size, icon, variant })];
    }

    if (adjustedCount === 0) {
      return [gambitTag('Only you', { size, icon, variant })];
    }

    if (adjustedCount === 1) {
      return [
        gambitTag(`You & ${localizeWithValues('gambit.detail.tag.player.present', [adjustedCount])}`, {
          size,
          icon,
          variant,
        }),
      ];
    }

    if (adjustedCount > 1) {
      return [
        gambitTag(`You & ${localizeWithValues('gambit.detail.tag.player.multiple', [adjustedCount])}`, {
          size,
          icon,
          variant,
        }),
      ];
    }
  }

  // No user attendance highlight (Ganbut detail use-case)
  if (isShort) {
    return [gambitTag(adjustedCount, { size, icon, variant })];
  }

  if (adjustedCount < 0) {
    return [];
  } else if (adjustedCount === 1) {
    return [
      gambitTag(localizeWithValues('gambit.detail.tag.player.present', [adjustedCount]), {
        size,
        icon,
        variant,
      }),
    ];
  } else if (adjustedCount > 1) {
    return [
      gambitTag(localizeWithValues('gambit.detail.tag.player.multiple', [adjustedCount]), {
        size,
        icon,
        variant,
      }),
    ];
  }

  return [];
}

export function getConfirmedPlayersTag(
  gambit: BaseGambitDto | GambitDetailDto,
  options?: PlayerTagOptions,
): GamChipType[] {
  const { gambitTag } = GambitHelper;

  if (!gambit) return [];

  const highlightAttendance =
    typeof options?.highlightAttendance === 'boolean' ? options.highlightAttendance : gambit.isAttending;

  // Defaults
  const icon = GamIconName.PAWN;
  let variant = GamChipVariant.DEFAULT;
  const { isShort, size } = options || {};

  const blockedCount =
    (gambit as GambitDetailDto).attendees?.filter((a) => a === null || a.username === 'unknown').length || 0;
  const confirmationCount = (gambit as GambitDetailDto).proposal?.confirmations?.length || 0;
  const isHighlightingCurrentUser = Boolean(highlightAttendance && gambit.isAttending && confirmationCount > 0);
  const adjustedCount = confirmationCount - (isHighlightingCurrentUser ? 1 : 0) - blockedCount;

  if (!isHighlightingCurrentUser && !adjustedCount) return [];

  // Highlight my attendance (Gambit list use-case)
  if (isHighlightingCurrentUser) {
    variant = GamChipVariant.PRIMARY;

    if (options?.isShort) {
      if (adjustedCount === 0) {
        return [gambitTag('You', { size, icon, variant })];
      }

      return [gambitTag(`You & ${adjustedCount}`, { size, icon, variant })];
    }

    if (adjustedCount === 0) {
      return [gambitTag('Only you', { size, icon, variant })];
    }

    if (adjustedCount === 1) {
      return [
        gambitTag(`You & ${localizeWithValues('gambit.detail.tag.player.present', [adjustedCount])}`, {
          size,
          icon,
          variant,
        }),
      ];
    }

    if (adjustedCount > 1) {
      return [
        gambitTag(`You & ${localizeWithValues('gambit.detail.tag.player.multiple', [adjustedCount])}`, {
          size,
          icon,
          variant,
        }),
      ];
    }
  }

  // No user attendance highlight (Ganbut detail use-case)
  if (isShort) {
    return [gambitTag(adjustedCount, { size, icon, variant })];
  }

  if (adjustedCount < 0) {
    return [];
  } else if (adjustedCount === 1) {
    return [
      gambitTag(localizeWithValues('gambit.detail.tag.player.present', [adjustedCount]), {
        size,
        icon,
        variant,
      }),
    ];
  } else if (adjustedCount > 1) {
    return [
      gambitTag(localizeWithValues('gambit.detail.tag.player.multiple', [adjustedCount]), {
        size,
        icon,
        variant,
      }),
    ];
  }

  return [];
}
export default class GambitHelper {
  static formatTime(
    time: string | Date,
    { omitTime, omitDay, omitCloseDays, shortDays }: FormatTimeOptions = {},
    timeZone?: string,
  ): string[] {
    const doFormat = timeZone
      ? (d: Date, form: string) => formatInTimeZone(d, timeZone, form)
      : (d: Date, form: string) => format(d, form);

    let atTime: string[] = [];

    const date = typeof time === 'string' ? new Date(time) : time;

    const START_OF_THE_WEEK = new Date().getDay() as Day;

    if (isToday(date) && !omitDay) {
      atTime = !omitCloseDays ? ['Today'] : [];
    } else if (isTomorrow(date) && !omitDay) {
      atTime = !omitCloseDays ? ['Tomorrow'] : [];
    } else if (isThisWeek(date, { weekStartsOn: START_OF_THE_WEEK }) && !omitDay) {
      atTime = [`This ${doFormat(date, shortDays ? 'EEE' : 'EEEE')}`];
    } else if (isNextWeek(date, START_OF_THE_WEEK) && !omitDay) {
      atTime = [`Next ${doFormat(date, shortDays ? 'EEE' : 'EEEE')}`];
    } else if (!omitDay) {
      atTime = [`${doFormat(date, 'EEE')}`, `${doFormat(date, 'LLL do')}`];
    }

    if (!omitTime) {
      atTime.push(doFormat(date, 'HH:mm'));
    }

    return atTime;
  }

  static isTomorrow(date: Date, now: Date = new Date()): boolean {
    const currentDate = new Date(now);
    currentDate.setDate(currentDate.getDate() + 1);
    return currentDate.toDateString() === date.toDateString();
  }

  private static isWithin30Minutes(date: Date, now: Date = new Date()) {
    const diffInMinutes = (now.getTime() - date.getTime()) / 1000 / 60; // Convert milliseconds to minutes
    return Math.abs(diffInMinutes) <= 30;
  }

  private static isWithinRange(date1: Date, date2?: Date | null, now: Date = new Date()): boolean {
    if (!date2) {
      const ninetyMinutesLater = new Date(date1.getTime() + 90 * 60 * 1000); // 90 minutes in milliseconds
      return now >= date1 && now <= ninetyMinutesLater;
    } else {
      return now >= date1 && now <= date2;
    }
  }

  static getGambitsDateType(
    startDate: string,
    isEnd: boolean,
    endDate?: string | null,
    now: Date = new Date(),
  ): GamDateType {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    if (this.isWithinRange(start, end, now) && !isEnd) {
      return GamDateType.NOW;
    } else if (this.isWithin30Minutes(start, now)) {
      return GamDateType.SOON;
    } else if (this.isTomorrow(start, now)) {
      return GamDateType.TOMORROW;
    } else if (now.toDateString() === start.toDateString()) {
      return GamDateType.TODAY;
    }

    return GamDateType.DAY;
  }

  static gambitTag(
    label: string | number,
    options?: { size?: GamChipSize; icon?: GamIconName; variant?: GamChipVariant },
  ): GamChipType {
    return {
      label: String(label).toUpperCase(),
      size: options?.size ?? GamChipSize.NORMAL,
      variant: options?.variant ?? GamChipVariant.DEFAULT,
      leftIcon: options?.icon,
    };
  }

  /* Mirrors when the gambit overlay offers its chat button, so unread indicators
     never point at a chat the player cannot open. */
  static isChatAccessible(
    gambit: { end?: string | Date | null; isAttending?: boolean; isChallenge?: boolean; isConfirmed?: boolean },
    now: Date = new Date(),
  ): boolean {
    const isConcluded = gambit.end ? now > new Date(gambit.end) : false;
    const isPendingChallenge = gambit.isChallenge && !gambit.isConfirmed;
    return !isConcluded && !isPendingChallenge && !!gambit.isAttending;
  }

  static getNewMessageTag(size?: GamChipSize): GamChipType {
    return {
      ...this.gambitTag(localize('gambit.detail.tag.new_message'), {
        size,
        icon: GamIconName.MESSAGE,
        variant: GamChipVariant.PRIMARY,
      }),
      isActive: true,
    };
  }

  private static getChallengeTag(size?: GamChipSize, isChallenger?: boolean, isEdited?: boolean): GamChipType[] {
    const icon = GamIconName.PAWN;
    let label = isChallenger
      ? localize('player.sections.respond.badge.challenge')
      : localize('gambit.detail.tag.player.challenge');

    if (isEdited) {
      label = localize('gambit.challenge.edited');
    }

    return [
      this.gambitTag(label, {
        size,
        icon,
        variant: GamChipVariant.PRIMARY,
      }),
    ];
  }

  private static getSparkTag(gambit: BaseGambitDto, size?: GamChipSize): GamChipType[] {
    const getLabel = (state?: GambitSparkState) => {
      switch (state) {
        case GambitSparkState.SPARKED:
          return 'Matching';
        case GambitSparkState.MATCHED:
          return 'Confirming';
        case GambitSparkState.CONFIRMED:
          return 'Booking';
        default:
          return 'Spark';
      }
    };

    return [
      this.gambitTag(localize(getLabel(gambit.sparkState)), {
        size,
        icon: GamIconName.SPARK,
        variant: GamChipVariant.PRIMARY,
      }),
    ];
  }

  private static getRatingTag(
    minEloRating?: number | null,
    maxEloRating?: number | null,
    size?: GamChipSize,
  ): GamChipType[] {
    if (!minEloRating || !maxEloRating) {
      return [];
    }

    if (minEloRating === maxEloRating) {
      return [this.gambitTag(String(minEloRating), { size, icon: GamIconName.GAMBIT })];
    }

    return [this.gambitTag(`${String(minEloRating)} - ${String(maxEloRating)}`, { size, icon: GamIconName.GAMBIT })];
  }

  private static getChessSetTag(gambit: DetailGambitDto, size?: GamChipSize): GamChipType[] {
    const { locationHasChessSets, locationChessSetCount, attendeeChessSetCount, attendeeCount } = gambit;

    const icon = GamIconName.CHESS_SET;

    let chessSetCount = attendeeChessSetCount;

    if (locationChessSetCount) {
      chessSetCount += locationChessSetCount;
    } else if (locationHasChessSets) {
      chessSetCount += 1;
    }

    if (chessSetCount === 0) {
      const neededLabel =
        attendeeCount > 2 ? 'gambit.detail.tag.chess_sets.needed' : 'gambit.detail.tag.chess_set.needed';
      return [this.gambitTag(localize(neededLabel), { size, icon })];
    }

    const label =
      chessSetCount > 1 // || (locationHasChessSets && !locationChessSetCount)
        ? 'gambit.detail.tag.chess_sets.count'
        : 'gambit.detail.tag.chess_set.count';

    const chessLabel = localizeWithValues(label, [
      locationHasChessSets && !locationChessSetCount ? `${chessSetCount}+` : chessSetCount,
    ]);

    return [this.gambitTag(chessLabel, { size, icon })];
  }

  private static getTypeTags(types: GambitType[], size?: GamChipSize, showAny?: boolean): GamChipType[] {
    if (types.includes(GambitType.ANY) && !showAny) return [];

    return types.map((type) => {
      const typeLocalized = localizeType(ObjectType.GAMBIT, type);
      return this.gambitTag(typeLocalized, { size });
    });
  }

  private static getCategoryTag(gambit: BaseGambitDto, size?: GamChipSize): GamChipType[] {
    const { gambitCategory: category } = gambit;

    /* A gambit standing in for a proposal has no category of its own, so there is
       nothing to label it with. */
    if (!category || category === GambitCategory.CASUAL || category === GambitCategory.SPARK) return [];

    const icon = category === GambitCategory.LESSON ? GamIconName.PLUS : undefined;
    return [this.gambitTag(localize(`gambit.detail.tag.category.${category.toLowerCase()}`), { size, icon })];
  }

  private static getTimedTag(size?: GamChipSize, timed?: boolean): GamChipType[] {
    if (!timed) return [];
    return [this.gambitTag(localize('gambit.detail.tag.category.timed'), { size })];
  }

  private static getStreamTag(size?: GamChipSize, streamed?: boolean): GamChipType[] {
    if (!streamed) return [];
    return [this.gambitTag(localize('gambit.detail.tag.category.streamed'), { size, icon: GamIconName.PLUS })];
  }

  static getRecurringTag(size?: GamChipSize, recurrenceInterval?: string, count?: number): GamChipType[] {
    if (!recurrenceInterval) return [];

    const label =
      recurrenceInterval === 'every_month'
        ? 'gambit.detail.tag.recurring.monthly'
        : recurrenceInterval === 'every_week'
          ? 'gambit.detail.tag.recurring.weekly'
          : 'gambit.detail.tag.recurring.daily';

    const fullLabel = count ? `${localize(label)}・${count}` : localize(label);

    return [this.gambitTag(fullLabel, { size, icon: GamIconName.REFRESH })];
  }

  static getGambitsTags(
    gambit: BaseGambitDto,
    options?: {
      size?: GamChipSize;
      isShort?: boolean;
      showChallenge?: boolean;
      showSpark?: boolean;
      showAttendees?: boolean;
      showConfirmations?: boolean;
      showRecurring?: boolean;
      showRating?: boolean;
      showCategory?: boolean;
      showChessSets?: boolean;
      showTypes?: boolean;
      showAny?: boolean;
      recurrenceCount?: number;
      subtractAttendees?: number;
      highlightAttendance?: boolean;
      isChallenger?: boolean;
    },
  ): GamChipType[] {
    if (!options) {
      options = {};
    }

    if (options.showAttendees === undefined) {
      options.showAttendees = true;
    }
    if (options.showRating === undefined) {
      options.showRating = true;
    }
    if (options.showCategory === undefined) {
      options.showCategory = true;
    }
    if (options.showTypes === undefined) {
      options.showTypes = true;
    }

    let result: GamChipType[] = [];

    const isInvitation = gambit.isChallenge && !gambit.isConfirmed;
    const isSpark = gambit.gambitCategory === GambitCategory.SPARK && !gambit.isConfirmed;

    if (isInvitation && options.showChallenge) {
      result = [...result, ...this.getChallengeTag(options.size, options.isChallenger, gambit.isEditedChallenge)];
    }

    if (isSpark && options.showSpark) {
      result = [...result, ...this.getSparkTag(gambit as BaseGambitDto, options.size)];
    }

    if (!isInvitation && options.showAttendees) {
      result = [
        ...result,
        ...getPlayerTag(gambit as BaseGambitDto, {
          size: options.size,
          isShort: options.isShort,
          highlightAttendance: options.highlightAttendance,
        }),
      ];
    }

    if (!isInvitation && options.showConfirmations) {
      result = [
        ...result,
        ...getConfirmedPlayersTag(gambit as BaseGambitDto, {
          size: options.size,
          isShort: options.isShort,
          highlightAttendance: options.highlightAttendance,
        }),
      ];
    }

    if (options.showRating && !(isSpark && !gambit.isConfirmed)) {
      result = [...result, ...this.getRatingTag(gambit.minEloRating, gambit.maxEloRating, options.size)];
    }

    if (options.showChessSets) {
      result = [...result, ...this.getChessSetTag(gambit as DetailGambitDto, options.size)];
    }

    if (options.showCategory) {
      result = [...result, ...this.getCategoryTag(gambit, options.size)];
    }

    if (options.showTypes) {
      result = [
        ...result,
        ...this.getTypeTags(gambit.gambitType, options.size, options.showAny),
        ...this.getTimedTag(options.size, gambit.timed),
        ...this.getStreamTag(options.size, gambit.streamed),
      ];
    }

    if (gambit.repeat !== GambitRepeat.NONE && options.showRecurring) {
      result = [...result, ...this.getRecurringTag(options.size, gambit.repeat, options.recurrenceCount)];
    }

    return result;
  }
}
